import { useMemo } from 'react';
import { useHistory } from 'react-router';

import type { Account, IFeatureMap } from '@feathr/blackbox';
import { EPackages, getFeatureMap, isLegacyPackage } from '@feathr/blackbox';

import { useLocalUrl, useRole } from '../state';
import { assertActivePackage } from './useLicense.utils';

// Package hierarchy from lowest to highest tier
const packageHierarchy = [
  EPackages.FeathrPlatformLite,
  EPackages.FeathrPlatformStarter,
  EPackages.FeathrPlatformBase,
  EPackages.FeathrPlatformEssential,
  EPackages.FeathrPlatformEssentialPro,
  EPackages.FeathrPlatformAdvanced,
  EPackages.FeathrPlatformAdvancedPro,
  EPackages.FeathrPlatformEnterprise,
  EPackages.FeathrPlatformEnterprisePro,
] as const;

export function useMinPackageForFeature(
  activePackage: Account['activePackage'] | undefined,
  featureKey: string,
  featureCategory?: keyof IFeatureMap,
): EPackages | null {
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { isSudoer } = useRole();
  assertActivePackage(activePackage, isSudoer, history, localUrl);

  return useMemo(() => {
    const checkFeatureInPackage = (pkg: EPackages): boolean => {
      const features = getFeatureMap(pkg);

      if (!features) {
        return false;
      }

      if (featureCategory) {
        const categoryFeatures = features[featureCategory];
        if (typeof categoryFeatures === 'object') {
          return categoryFeatures[featureKey] > 0 || categoryFeatures[featureKey] === true;
        }
        return false;
      }

      // Search through all categories if no specific category is provided
      return Object.values(features).some((category) => {
        if (typeof category === 'object') {
          return category[featureKey] > 0 || category[featureKey] === true;
        }
        return false;
      });
    };

    // Check if the current package already has the feature
    if (checkFeatureInPackage(activePackage?.name as EPackages)) {
      // User already has access
      return null;
    }

    // Find the minimum modern package that supports the feature
    // This ensures we never suggest upgrading to a legacy package
    const minimumPackage = packageHierarchy.find(checkFeatureInPackage);

    // Returns null if no modern package supports the feature
    if (!minimumPackage) {
      return null;
    }

    // For modern packages, check if they need to upgrade to a higher tier
    if (!isLegacyPackage(activePackage?.name as EPackages)) {
      const currentIndex = packageHierarchy.indexOf(
        activePackage?.name as (typeof packageHierarchy)[number],
      );
      const requiredIndex = packageHierarchy.indexOf(minimumPackage);

      // Returns the minimum package only if user needs to upgrade
      return currentIndex < requiredIndex ? minimumPackage : null;
    }

    // For legacy packages, always return the minimum modern package
    // (since they're not in packageHierarchy, they always need to upgrade)
    return minimumPackage;
  }, [activePackage, featureKey, featureCategory]);
}
